import { Button, Divider, Grid, IconButton, SvgIcon, Typography } from '@mui/material';
import { get } from 'lodash';
import { ComponentType, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import { getTranslatedValue } from 'src/common/utils/format-utils';
import { BaseDialog } from 'src/components/dialogs/base-dialog/base-dialog';
import { dateFormat } from '../../../hooks';
import { clearSchedule, getSchedule } from '../../../thunks/schedule-thunks';
import { CalendarIcon, ClockIcon, OfficeIcon, UsersIcon } from '../../icons';
import { Loader } from '../../loader';
import useStyles from './styles';

interface AttestationsInfoDialogProps {
  title?: string;
  id?: string;
  onEdit?: (id: string) => void;
  popDialog?: any;
}

export const AttestationsInfoDialog: ComponentType<AttestationsInfoDialogProps> = ({
  title: dialogTitle,
  id,
  onEdit,
  popDialog,
  ...props
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const { one } = useSelector((state: RootStateOrAny) => state.schedule);

  const title = dialogTitle ? dialogTitle : t('attestations.info', 'Информация об аттестации');
  const data = get(one, 'data', {});

  const view = () => {
    const isLoading = get(one, 'isLoading', false);

    if (isLoading) return <Loader minHeight="100px" />;
    if (data) {
      const oneDate = new Date(data.date);
      const startDate = dateFormat(oneDate, 'D F Y');
      const startTime = dateFormat(oneDate, 'T');
      const endTime = dateFormat(new Date(oneDate.setHours(oneDate.getHours() + 1)), 'T');

      const organization = get(data, ['organization', 'title'], null);
      const total = get(data, 'total', null);
      const left = get(data, 'left', null);

      return (
        <>
          {(startDate || startTime || organization || total || left) && (
            <Grid container direction="column" spacing={1.5}>
              {startDate && (
                <Grid item>
                  <Grid container columnSpacing={2} alignItems="center">
                    <Grid item>
                      <IconButton
                        component="div"
                        color="primary"
                        size="small"
                        className={classes.iconButton}
                      >
                        <SvgIcon component={CalendarIcon} viewBox="0 0 24 24" />
                      </IconButton>
                    </Grid>
                    <Grid item>
                      <Typography variant="subtitle2">{startDate}</Typography>
                    </Grid>
                  </Grid>
                </Grid>
              )}
              {startTime && endTime && (
                <Grid item>
                  <Grid container columnSpacing={2} alignItems="center">
                    <Grid item>
                      <IconButton
                        component="div"
                        color="primary"
                        size="small"
                        className={classes.iconButton}
                      >
                        <SvgIcon component={ClockIcon} viewBox="0 0 24 24" />
                      </IconButton>
                    </Grid>
                    <Grid item>
                      <Typography variant="subtitle2">
                        {startTime} - {endTime}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              )}
              {organization && (
                <Grid item>
                  <Grid container columnSpacing={2} alignItems="center">
                    <Grid item>
                      <IconButton
                        component="div"
                        color="primary"
                        size="small"
                        className={classes.iconButton}
                      >
                        <SvgIcon component={OfficeIcon} viewBox="0 0 24 24" />
                      </IconButton>
                    </Grid>
                    <Grid item>
                      <Typography variant="subtitle2">
                        {getTranslatedValue(organization, i18n.language)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              )}
              {(total || left) && (
                <Grid item>
                  <Grid container columnSpacing={2} alignItems="center">
                    <Grid item>
                      <IconButton
                        component="div"
                        color="primary"
                        size="small"
                        className={classes.iconButton}
                      >
                        <SvgIcon component={UsersIcon} viewBox="0 0 24 24" />
                      </IconButton>
                    </Grid>
                    <Grid item>
                      <Typography variant="subtitle2" component="span">
                        {t('people.count', `${total} человек`, { total })}
                      </Typography>{' '}
                      <Typography variant="subtitle2" color="grey.400" component="span">
                        ({t('place.left', `${left} свободных мест`, { left })})
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </Grid>
          )}
          {onEdit && (
            <>
              <Divider sx={{ mt: 3, mb: 3 }} />
              <Grid container columnSpacing={2} justifyContent="space-between">
                <Grid item>
                  <Button onClick={popDialog} variant="outlined" size="large">
                    {t('cancel', 'Отмена')}
                  </Button>
                </Grid>
                <Grid item>
                  <Button onClick={handleEdit} variant="contained" color="secondary" size="large">
                    {t('action.edit', 'Редактировать')}
                  </Button>
                </Grid>
              </Grid>
            </>
          )}
        </>
      );
    }
    return null;
  };

  const handleEdit = () => {
    if (onEdit && data) {
      onEdit(data.id);
      dispatch(clearSchedule());
    }
  };

  useEffect(() => {
    if (id) dispatch(getSchedule(id, { embed: 'organization' }));
  }, [dispatch, id]);

  return (
    <BaseDialog {...{ ...props, popDialog }}>
      {title && (
        <Typography variant="h2" component="h6" mb={2}>
          {title}
        </Typography>
      )}
      {view()}
    </BaseDialog>
  );
};
